import type { ApplicationContext } from '@hc-frontend/core-context';
import { fetchBasedCall } from '@hc-frontend/core-utils-net';
import type { ZodType } from 'zod';

/**
 * Error thrown when the AdButler API returns a non-2xx response.
 */
export class AdButlerApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly endpoint: string,
    message: string,
  ) {
    super(`AdButler API error [${status}] ${endpoint}: ${message}`);
    this.name = 'AdButlerApiError';
  }
}

interface GetInput<T> {
  path: string;
  queryParams?: Record<string, string | number | undefined>;
  responseSchema?: ZodType<T>;
}

/**
 * Provides methods for making authenticated requests to the AdButler API.
 */
export interface AdButlerPortalApi {
  /** Sends a GET request; when `schema` is provided, validates the response body against it. */
  get<T>(input: GetInput<T>): Promise<T>;

  /** Sends a POST request with a JSON body; when `schema` is provided, validates the response body against it. */
  post<T>(path: string, body: unknown, schema?: ZodType<T>): Promise<T>;

  /** Sends a PUT request with a JSON body; when `schema` is provided, validates the response body against it. */
  put<T>(path: string, body: unknown, schema?: ZodType<T>): Promise<T>;

  /** Sends a DELETE request and resolves when the request completes. */
  delete(path: string): Promise<void>;

  /** Sends a multipart POST request; when `schema` is provided, validates the response body against it. */
  postMultipart<T>(
    path: string,
    formData: FormData,
    schema?: ZodType<T>,
  ): Promise<T>;
}

/**
 * Factory that creates an AdButler Portal API caller.
 * Follows the same pattern as getAdButlerAds — receives baseUrl, apiKey, and log
 * from the server composition root; never exposes credentials to the client.
 */
export function createAdButlerPortalApi(
  baseUrl: string,
  apiKey: string,
  log: ApplicationContext['log'],
): AdButlerPortalApi {
  const normalizedBase = baseUrl.replace(/\/$/, '');
  const authHeader = `Basic ${apiKey}`;

  /**
   * A schema drift in AdButler shouldn't take down the Advertiser Portal.
   * There is a monitor for the error logs, so the issue will be noticed and can be fixed.
   */
  function parseResponseBody<T>(
    body: T | undefined,
    responseStatus: number,
    schema: ZodType<T>,
    method: string,
    path: string,
  ): T {
    const parsingResult = schema.safeParse(body);

    if (!parsingResult.success) {
      log({
        type: 'error',
        context: 'server',
        message: 'adbutlerPortalApiError',
        data: {
          method,
          path,
          status: responseStatus,
          errorMessage: parsingResult.error.message,
          rawBody: body,
        },
      });
      return body as T;
    }
    return parsingResult.data;
  }

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    queryParams?: Record<string, string | number | undefined>,
    schema?: ZodType<T>,
  ): Promise<T> {
    const url = `${normalizedBase}${path}`;

    const filteredQuery: Record<string, string> = {};
    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        if (value !== undefined) {
          filteredQuery[key] = String(value);
        }
      }
    }

    log({
      type: 'info',
      context: 'server',
      message: 'adbutlerPortalApiRequest',
      data: {
        method,
        path,
        queryParams: filteredQuery,
        ...(body !== undefined && { body }),
      },
    });

    const result = await fetchBasedCall<unknown, T>({
      url,
      method: method as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
      body: body as unknown,
      headers: { Authorization: authHeader },
      queryParams:
        Object.keys(filteredQuery).length > 0 ? filteredQuery : undefined,
    });

    if (result.status === 204) {
      return undefined as T;
    }

    if (result.status < 200 || result.status >= 300) {
      const errorBody = result.body as {
        errors?: { message: string }[];
        message?: string;
        parameters?: unknown;
      };
      const message =
        errorBody?.errors?.[0]?.message ??
        errorBody?.message ??
        String(result.status);

      log({
        type: 'error',
        context: 'server',
        message: 'adbutlerPortalApiError',
        data: {
          method,
          path,
          status: result.status,
          errorMessage: message,
          parameters: errorBody?.parameters,
          rawBody: errorBody,
        },
      });

      throw new AdButlerApiError(result.status, path, message);
    }

    log({
      type: 'info',
      context: 'server',
      message: 'adbutlerPortalApiSuccess',
      data: { method, path, status: result.status },
    });

    if (!schema) {
      return result.body as T;
    }

    return parseResponseBody<T>(
      result.body,
      result.status,
      schema,
      method,
      path,
    );
  }

  return {
    get<T>({ path, queryParams, responseSchema }: GetInput<T>): Promise<T> {
      return request<T>('GET', path, undefined, queryParams, responseSchema);
    },

    post<T>(path: string, body: unknown, schema?: ZodType<T>): Promise<T> {
      return request<T>('POST', path, body, undefined, schema);
    },

    put<T>(path: string, body: unknown, schema?: ZodType<T>): Promise<T> {
      return request<T>('PUT', path, body, undefined, schema);
    },

    delete(path: string): Promise<void> {
      return request<void>('DELETE', path);
    },

    async postMultipart<T>(
      path: string,
      formData: FormData,
      schema?: ZodType<T>,
    ): Promise<T> {
      const url = `${normalizedBase}${path}`;

      log({
        type: 'info',
        context: 'server',
        message: 'adbutlerPortalApiRequest',
        data: { method: 'POST multipart', path },
      });

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: authHeader,
          // Content-Type must NOT be set — fetch sets it with the boundary
        },
        body: formData,
      });

      if (!response.ok) {
        let message = response.statusText;
        let parameters: unknown;
        try {
          const err = await response.json();
          message = err?.errors?.[0]?.message ?? err?.message ?? message;
          parameters = err?.parameters;
        } catch {
          // ignore JSON parse error
        }

        log({
          type: 'error',
          context: 'server',
          message: 'adbutlerPortalApiError',
          data: {
            method: 'POST multipart',
            path,
            status: response.status,
            errorMessage: message,
            ...(parameters !== undefined && { parameters }),
          },
        });

        throw new AdButlerApiError(response.status, path, message);
      }

      const responseBody = await response.json();

      log({
        type: 'info',
        context: 'server',
        message: 'adbutlerPortalApiSuccess',
        data: { method: 'POST multipart', path, status: response.status },
      });

      if (!schema) {
        return responseBody as T;
      }

      return parseResponseBody(
        responseBody,
        response.status,
        schema,
        'POST',
        path,
      );
    },
  };
}
